import * as React from "react";
import { Radio, RotateCw, Send, Unplug, MessageSquareWarning } from "lucide-react";

import { SCENE_URL } from "@/config";
import { report } from "@/lib/report";
import { toast } from "sonner";
import {
  parseEnvelope,
  isHello,
  hostReducer,
  initialHostState,
  type HelloParams,
  type SurfaceCommand,
} from "./surface-protocol";
import { createSceneHost, type SceneContext } from "./scene-host";

/** How long we wait for the guest's `surface/hello` before degrading to the
 *  honest offline placard. Generous enough for a cold games dev server. */
const HANDSHAKE_TIMEOUT_MS = 8000;

/** Logical viewport the scene renders at, scaled to fit the portal column — the
 *  same device-frame trick BrandPreviewFrame uses for the live app preview. */
const DEVICE = { width: 390, height: 720 };
const SCALE = 0.86;

/**
 * The portal panel that hosts a **living scene** (Surface Protocol, first impl).
 * Frames a games-served scene in an iframe, runs the capability handshake (guest
 * declares → host drives typed commands via the ./scene-host seam), and — when
 * the scene server is down — shows an honest placard naming the URL. Never fakes
 * scene chrome.
 *
 * This component is the thin DOM shell; the lifecycle + contract live in
 * ./surface-protocol (pure, unit-tested) and the command instrumentation in
 * ./scene-host (one analytics event per command).
 */
export function SceneFrame({
  url = SCENE_URL,
  brandId = null,
  route = "/dev/scene",
  viewer = "dev",
}: {
  url?: string;
  brandId?: string | null;
  route?: string;
  viewer?: string;
}) {
  const origin = React.useMemo(() => {
    try {
      return new URL(url).origin;
    } catch {
      return url;
    }
  }, [url]);

  const [state, dispatch] = React.useReducer(hostReducer, url, initialHostState);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  // Transport: post an envelope to the framed guest at its pinned origin (never
  // `*` — BrandPreviewFrame's discipline). Stable across renders.
  const postToGuest = React.useCallback(
    (env: SurfaceCommand) => iframeRef.current?.contentWindow?.postMessage(env, origin),
    [origin],
  );

  // One command host per (re)mount so correlation ids restart cleanly.
  const hostRef = React.useRef(createSceneHost({ post: postToGuest }));
  React.useEffect(() => {
    hostRef.current = createSceneHost({ post: postToGuest });
  }, [state.attempt, postToGuest]);

  // Listen for the guest's handshake. Origin- AND source-pinned: accept messages
  // only from THIS iframe at the expected origin (guards against other embeds /
  // extensions / the app's own legacy popin:theme-preview messages).
  React.useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== origin) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      const env = parseEnvelope(e.data);
      if (!env) return;
      if (isHello(env)) {
        const { capabilities, scene } = env.params as HelloParams;
        dispatch({ type: "hello", capabilities, scene });
      }
      // Command results/errors would be routed to pending sends here in a fuller
      // impl; v0 fire-and-forwards commands (the seam is proven headless).
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [origin]);

  // Handshake timer: if no hello lands within the window, degrade to the placard.
  // Keyed on `attempt` so a Reload restarts it.
  React.useEffect(() => {
    const id = setTimeout(() => dispatch({ type: "timeout" }), HANDSHAKE_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [state.attempt]);

  const ctx: SceneContext = { capabilities: state.capabilities, scene: state.scene };

  // On connect, hand the guest its context — the host's first driven command.
  // This is also the connection signal: it fires exactly once, only when live, and
  // emits one "Scene Command Sent" event via the seam (no UI-level track() — the
  // tracking plan's discipline). Once per attempt.
  const contextSentForAttempt = React.useRef(-1);
  React.useEffect(() => {
    if (state.phase !== "live") return;
    if (contextSentForAttempt.current === state.attempt) return;
    contextSentForAttempt.current = state.attempt;
    hostRef.current.setContext(ctx, { brandId, route, viewer });
  }, [state.phase, state.attempt]);

  function reload() {
    dispatch({ type: "mount", url });
  }

  function filePlacardReport() {
    report({
      surface: "scene",
      route,
      brandId,
      session: "dev",
      user: viewer,
      category: "bug",
      message: `Scene panel: scene at ${url} was offline (${state.reason ?? "unknown"}).`,
    });
    toast.success("Thanks — your report was filed.");
  }

  const innerW = Math.round(DEVICE.width * SCALE);
  const innerH = Math.round(DEVICE.height * SCALE);

  return (
    <div className="mx-auto max-w-[520px]">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[.06em] text-text-3">
          <Radio className={state.phase === "live" ? "size-3.5 text-brand" : "size-3.5"} />
          SCENE — dev preview
          {state.phase === "live" && state.scene && (
            <span className="normal-case tracking-normal text-muted-foreground">· {state.scene.name}</span>
          )}
        </div>
        <button
          type="button"
          onClick={reload}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-panel-2 px-2.5 py-1 text-[11.5px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          <RotateCw className="size-3.5" /> Reload
        </button>
      </div>

      {state.phase === "offline" ? (
        <OfflinePlacard url={url} reason={state.reason} onReload={reload} onReport={filePlacardReport} />
      ) : (
        <div className="grid place-items-center">
          <div
            className="relative overflow-hidden rounded-[28px] bg-[#0a0a0f]"
            style={{ width: innerW, height: innerH, boxShadow: "0 24px 60px rgba(0,0,0,.45), inset 0 0 0 1px rgba(255,255,255,.08)" }}
          >
            <iframe
              ref={iframeRef}
              key={state.attempt}
              title="Scene preview"
              src={url}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: DEVICE.width,
                height: DEVICE.height,
                border: 0,
                transform: `scale(${SCALE})`,
                transformOrigin: "top left",
              }}
              allow="clipboard-write; fullscreen; autoplay"
            />
            {state.phase === "connecting" && (
              <div className="absolute inset-0 grid place-items-center bg-[#0a0a0f] text-center">
                <div>
                  <div className="mb-1 text-[12px] font-semibold text-white/60">Connecting to scene…</div>
                  <div className="text-[11px] text-white/35">{origin}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Host command controls — each drives a typed command through the seam
          (one analytics event each). Only live when the guest declared support. */}
      {state.phase === "live" && (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          <SceneCommandButton
            label="Show arrival scene"
            icon={<Send className="size-3.5" />}
            disabled={!ctx.capabilities?.commands.includes("scene/present")}
            onClick={() => hostRef.current.present(ctx, { beat: 0 })}
          />
          <SceneCommandButton
            label="Test connection"
            icon={<Radio className="size-3.5" />}
            disabled={!ctx.capabilities?.commands.includes("scene/ping")}
            onClick={() => hostRef.current.ping(ctx)}
          />
        </div>
      )}

      <div className="mt-2.5 text-center text-[11.5px] text-text-3">
        First implementation of the one guest-surface contract — the panel frames a
        real games-served scene and drives it with typed commands.
      </div>
    </div>
  );
}

function SceneCommandButton({
  label,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-[10px] border border-border bg-panel-2 px-3 py-1.5 text-[12.5px] font-semibold text-foreground transition-colors hover:brightness-110 disabled:opacity-40"
    >
      {icon} {label}
    </button>
  );
}

/** Honest degradation: names the URL, says it's offline, offers a retry and the
 *  report seam. NO fake scene chrome — the panel tells the truth. */
function OfflinePlacard({
  url,
  reason,
  onReload,
  onReport,
}: {
  url: string;
  reason: string | null;
  onReload: () => void;
  onReport: () => void;
}) {
  return (
    <div className="grid place-items-center rounded-[28px] border border-dashed border-border bg-panel-2/40 px-6 py-12 text-center">
      <div className="mb-4 grid size-12 place-items-center rounded-full bg-panel-2 text-text-3">
        <Unplug className="size-6" />
      </div>
      <div className="mb-1 text-[15px] font-bold text-foreground">Scene is offline</div>
      <p className="mb-1 max-w-[340px] text-[12.5px] text-muted-foreground">
        Nothing is answering the Surface Protocol handshake at
      </p>
      <code className="mb-3 block max-w-[380px] break-all rounded-md bg-field px-2.5 py-1.5 text-[12px] text-foreground">
        {url}
      </code>
      {reason && <p className="mb-4 text-[11.5px] text-text-3">({reason})</p>}
      <p className="mb-5 max-w-[340px] text-[12px] text-text-3">
        Start the games scene dev server, then reload — the panel will connect and the
        live scene will appear here.
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReload}
          className="inline-flex items-center gap-1.5 rounded-[10px] bg-brand-gradient px-4 py-2 text-[13px] font-semibold text-white shadow-[0_2px_14px_rgba(124,58,237,.35)] transition-[filter] hover:brightness-110"
        >
          <RotateCw className="size-3.5" /> Reload
        </button>
        <button
          type="button"
          onClick={onReport}
          className="inline-flex items-center gap-1.5 rounded-[10px] border border-border bg-panel-2 px-4 py-2 text-[13px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          <MessageSquareWarning className="size-3.5" /> Report
        </button>
      </div>
    </div>
  );
}
